// Single gtag.js instance shared by Google Ads (conversions + enhanced
// conversions) and Google Analytics 4. The loader and the Ads `config` live in
// index.html because Ads requires the tag in the head; everything that depends on
// app state (consent updates, user-provided data, purchase events) lives here.

import { isAuditBrowser } from './auditBrowser';
import { setChatGPTConsent } from './chatgptAds';

type GtagArgs =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['set', string, unknown]
  | ['event', string, Record<string, unknown>?]
  | ['consent', 'default' | 'update', Record<string, unknown>];

const RAW_GOOGLE_ADS_ID = typeof import.meta.env.VITE_GOOGLE_ADS_ID === 'string'
  ? import.meta.env.VITE_GOOGLE_ADS_ID.trim()
  : '';
const RAW_CONVERSION_SEND_TO = typeof import.meta.env.VITE_GOOGLE_ADS_CONVERSION_SEND_TO === 'string'
  ? import.meta.env.VITE_GOOGLE_ADS_CONVERSION_SEND_TO.trim()
  : '';
const RAW_SIGNUP_SEND_TO = typeof import.meta.env.VITE_GOOGLE_ADS_SIGNUP_SEND_TO === 'string'
  ? import.meta.env.VITE_GOOGLE_ADS_SIGNUP_SEND_TO.trim()
  : '';
const RAW_GA4_ID = typeof import.meta.env.VITE_GA4_MEASUREMENT_ID === 'string'
  ? import.meta.env.VITE_GA4_MEASUREMENT_ID.trim()
  : '';

export const GOOGLE_ADS_ID = RAW_GOOGLE_ADS_ID || 'AW-18360585902';
// Conversion action "Compra Culqi" from Google Ads. Full send_to = AW-ID/label.
// "Compra (1)" (HiH6CNKJntkcEK6dgbNE) carries months of pageview hits and is kept
// secondary, so real payments must not be reported to it.
export const GOOGLE_ADS_CONVERSION_SEND_TO = RAW_CONVERSION_SEND_TO
  || (GOOGLE_ADS_ID ? `${GOOGLE_ADS_ID}/TghmCKy-vZodEK6dgbNE` : '');
// Sign-up conversion action. No default: without a label of its own, a registration
// must never reach Ads under the purchase action.
export const GOOGLE_ADS_SIGNUP_SEND_TO = /^AW-\d+\/[\w-]+$/.test(RAW_SIGNUP_SEND_TO) ? RAW_SIGNUP_SEND_TO : '';
// Data stream "radar bi" of the Analytics property, tagged by the same loader as Ads.
export const GA4_MEASUREMENT_ID = /^G-[A-Z0-9]+$/i.test(RAW_GA4_ID) ? RAW_GA4_ID : 'G-SZWF6FQEFR';

// dataLayer event a GTM-managed Ads tag can trigger on to pick up user data.
export const GOOGLE_USER_DATA_EVENT = 'radarbi_user_data';
export const GOOGLE_CONSENT_STORAGE_KEY = 'radarbi_google_consent_v2';
export const GOOGLE_CONSENT_OPEN_EVENT = 'radarbi:open-google-consent';
// Google Ads' URL-based purchase goal counts visits to /purchase. The SPA only puts
// that path in the URL once the payment has been settled by the backend.
export const PURCHASE_PATH = '/purchase';

// Stable DOM id / globals for Ads automatic collection and JS-variable setups.
// URL-based conversion actions cannot rely on the code snippet alone — Google only
// supports automatic detection, CSS selectors or JS variables for those — so the
// signed-in email must also exist on the page when the /purchase hit fires.
export const ENHANCED_CONVERSION_EMAIL_ELEMENT_ID = 'radarbi-ec-email';

// Peru is the only market we sell in, so a local number without a prefix is +51.
const DEFAULT_PHONE_COUNTRY_CODE = '51';
const PERU_LOCAL_PHONE_LENGTH = 9;

export type GoogleConsentChoice = 'granted' | 'denied';

// Only the fields Google can accept on their own. Names and address parts are
// deliberately absent: Ads rejects an address block unless first name, last name,
// postal code and country all come with it, and a rejected block is what shows up
// in the UI as "your user data is not formatted correctly".
export type GoogleUserData = {
  email?: string;
  phone_number?: string;
};

export type GoogleUserIdentity = {
  email?: string | null;
  phone?: string | null;
};

const trackedGoogleAdsConversions = new Set<string>();
let tagsConfigured = false;
let currentUserData: GoogleUserData | null = null;
let currentUserDataKey = '';

export function gtag(...args: GtagArgs) {
  if (typeof window === 'undefined') return;
  const globals = window as any;
  globals.dataLayer = globals.dataLayer || [];
  if (typeof globals.gtag !== 'function') {
    // Matches the official snippet: the queue holds the raw `arguments` object.
    globals.gtag = function gtagShim() { globals.dataLayer.push(arguments); };
  }
  globals.gtag(...args);
}

function requestMarketingLoader() {
  if (typeof window === 'undefined') return;
  if (isAuditBrowser()) return;
  const load = (window as any).__radarbiLoadMarketing;
  if (typeof load === 'function') load();
}

// index.html loads gtag.js and configures both destinations, so normally there is
// nothing to do here. This only rebuilds the setup if a proxy or cache ever strips
// the head snippet: configuring a destination twice replays its page-load ping,
// which for Ads is a duplicate hit on the URL-based conversion goal.
export function ensureGoogleTags() {
  if (typeof window === 'undefined' || tagsConfigured) return;
  const primaryId = GOOGLE_ADS_ID || GA4_MEASUREMENT_ID;
  if (!primaryId) return;
  tagsConfigured = true;

  // Queue config only. The remote gtag.js is loaded from index.html after the
  // visitor engages, so a Lighthouse cold load never pays for Ads cookies/JS.
  if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return;
  gtag('js', new Date());
  if (GOOGLE_ADS_ID) {
    gtag('config', GOOGLE_ADS_ID, { allow_enhanced_conversions: true });
  }
  if (GA4_MEASUREMENT_ID) gtag('config', GA4_MEASUREMENT_ID);
}

export function updateGoogleConsent(choice: GoogleConsentChoice) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(GOOGLE_CONSENT_STORAGE_KEY, choice);
  } catch {
    // Still update the current page when browser storage is unavailable.
  }
  requestMarketingLoader();
  gtag('consent', 'update', {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });
  setChatGPTConsent(choice === 'granted');
}

// gtag hashes and normalizes on its own, but it forwards whatever it is given: an
// empty string, a display name or a national phone number all reach Ads as
// unusable data. Returning '' for those keeps them out of the payload entirely.
export function normalizeGoogleEmail(value?: string | null): string {
  if (typeof value !== 'string') return '';
  const email = value.trim().toLowerCase();
  if (email.length > 254) return '';
  return /^[^\s@,;<>()[\]"]+@[^\s@,;<>()[\]"]+\.[a-z]{2,}$/.test(email) ? email : '';
}

// E.164: a leading + and 8 to 15 digits, no separators.
export function normalizeGooglePhone(value?: string | null): string {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';

  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (!raw.startsWith('+')) {
    if (digits.startsWith('00')) {
      digits = digits.slice(2);
    } else {
      const local = digits.replace(/^0+/, '');
      const hasCountryCode = local.length > PERU_LOCAL_PHONE_LENGTH
        && local.startsWith(DEFAULT_PHONE_COUNTRY_CODE);
      digits = hasCountryCode ? local : `${DEFAULT_PHONE_COUNTRY_CODE}${local}`;
    }
  }

  return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : '';
}

export function buildGoogleUserData(identity: GoogleUserIdentity): GoogleUserData | null {
  const data: GoogleUserData = {};
  const email = normalizeGoogleEmail(identity.email);
  if (email) data.email = email;
  const phone = normalizeGooglePhone(identity.phone);
  if (phone) data.phone_number = phone;
  return Object.keys(data).length > 0 ? data : null;
}

// Mirror the signed-in identity into places Ads automatic / CSS / JS-variable
// collection can read. Never leave empty values in those slots — an empty email
// field is exactly the diagnostic "fields are being sent but contain no data".
function syncEnhancedConversionSurfaces(data: GoogleUserData | null) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const globals = window as any;

  if (data?.email) {
    globals.__radarbiUserData = { ...data };
    globals.radarbiEnhancedConversionEmail = data.email;
  } else {
    delete globals.__radarbiUserData;
    delete globals.radarbiEnhancedConversionEmail;
  }

  let el = document.getElementById(ENHANCED_CONVERSION_EMAIL_ELEMENT_ID) as HTMLInputElement | null;
  if (!data?.email) {
    el?.remove();
    return;
  }

  if (!el) {
    el = document.createElement('input');
    el.type = 'email';
    el.id = ENHANCED_CONVERSION_EMAIL_ELEMENT_ID;
    el.name = 'email';
    el.setAttribute('autocomplete', 'email');
    el.setAttribute('aria-label', 'Correo para conversiones');
    el.tabIndex = -1;
    // Off-screen (not display:none): automatic detection and CSS selectors still see it.
    // Do not use aria-hidden on a focusable input — Lighthouse flags it.
    el.style.cssText = 'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;';
    document.body.appendChild(el);
  }
  el.value = data.email;
}

function applyGoogleUserData(data: GoogleUserData | null): GoogleUserData | null {
  const key = data ? JSON.stringify(data) : '';
  if (key === currentUserDataKey) {
    // Still refresh DOM/globals even when the hashed payload is unchanged — a soft
    // navigation can drop the helper input without clearing the in-memory cache.
    syncEnhancedConversionSurfaces(currentUserData);
    return currentUserData;
  }

  currentUserDataKey = key;
  currentUserData = data;
  ensureGoogleTags();
  syncEnhancedConversionSurfaces(data);

  // An empty object clears the previous value: after a logout the next visitor on
  // this page must not be measured as the account that just signed out.
  gtag('set', 'user_data', data ?? {});

  // The GTM container shares this dataLayer. Only publish populated user_data —
  // pushing null/empty fields makes a GTM User-Provided Data variable report the
  // exact "empty enhanced conversion fields" error Ads is showing.
  const globals = window as any;
  globals.dataLayer = globals.dataLayer || [];
  if (data) {
    globals.dataLayer.push({ event: GOOGLE_USER_DATA_EVENT, user_data: data });
  } else {
    globals.dataLayer.push({ event: 'radarbi_user_data_clear' });
  }

  return data;
}

// Enhanced conversions read whatever `user_data` was last set on the page, so this
// has to run before any conversion hit — including the page_view that feeds the
// URL-based goal. Consent Mode still decides whether it is transmitted.
// An identity with nothing usable leaves the current value alone: a caller that
// simply does not know the email must not erase what sign-in already provided.
export function setGoogleUserData(identity: GoogleUserIdentity): GoogleUserData | null {
  if (typeof window === 'undefined') return null;
  const data = buildGoogleUserData(identity);
  return data ? applyGoogleUserData(data) : currentUserData;
}

export function clearGoogleUserData() {
  if (typeof window === 'undefined') return;
  applyGoogleUserData(null);
}

export function getGoogleUserData(): GoogleUserData | null {
  return currentUserData;
}

function userDataEventParams(data: GoogleUserData | null): Record<string, unknown> {
  return data ? { user_data: data } : {};
}

// A SPA never really navigates, so the URL-based Ads goal is fed by briefly swapping
// the address to PURCHASE_PATH and sending an explicit page_view for it.
// Call this only once the charge is settled: this hit is what the goal counts, so
// sending it on checkout intent would report shoppers who never paid. The address is
// put back as soon as the hit is queued, because leaving /purchase in the URL lets a
// reload or a shared link count another purchase. replaceState (not pushState) keeps
// the shopper's history, and the back button, untouched.
export function sendPurchasePageView(identity: GoogleUserIdentity = {}) {
  if (typeof window === 'undefined') return;
  const previous = `${window.location.pathname}${window.location.search}`;
  const alreadyOnPurchaseURL = window.location.pathname === PURCHASE_PATH;

  requestMarketingLoader();
  ensureGoogleTags();
  // Must precede the page_view below, which is the hit the URL-based goal counts.
  const userData = setGoogleUserData(identity) || getGoogleUserData();

  if (!alreadyOnPurchaseURL) {
    window.history.replaceState({ radarbiPurchase: true }, '', `${PURCHASE_PATH}${window.location.search}`);
  }
  try {
    gtag('event', 'page_view', {
      // GA4 tracks history changes through enhanced measurement, so this manual hit
      // is scoped to Ads to avoid counting the same view twice.
      ...(GOOGLE_ADS_ID ? { send_to: GOOGLE_ADS_ID } : {}),
      page_path: PURCHASE_PATH,
      // Passed explicitly, so the goal still matches after the address is restored.
      page_location: window.location.href,
      page_title: document.title,
      ...userDataEventParams(userData),
    });
  } finally {
    if (!alreadyOnPurchaseURL) {
      window.history.replaceState(null, '', previous);
    }
  }
}

// Only for an account the backend just created; returning users signing in again are
// not registrations.
export function trackGoogleSignUp(params: {
  userId: string;
  method: string;
  email?: string | null;
  offerCredits?: number;
}) {
  if (typeof window === 'undefined' || !params.userId) return;
  const dedupeKey = `signup:${params.userId}`;
  if (trackedGoogleAdsConversions.has(dedupeKey)) return;
  trackedGoogleAdsConversions.add(dedupeKey);

  requestMarketingLoader();
  ensureGoogleTags();
  const userData = setGoogleUserData({ email: params.email }) || getGoogleUserData();

  if (GOOGLE_ADS_SIGNUP_SEND_TO) {
    gtag('event', 'conversion', {
      send_to: GOOGLE_ADS_SIGNUP_SEND_TO,
      transaction_id: dedupeKey,
      ...userDataEventParams(userData),
    });
  }

  if (GA4_MEASUREMENT_ID) {
    gtag('event', 'sign_up', {
      send_to: GA4_MEASUREMENT_ID,
      method: params.method,
      ...(params.offerCredits != null ? { offer_credits: params.offerCredits } : {}),
    });
  }
}

export function trackGooglePurchase(params: {
  value: number;
  currency: string;
  transactionId: string;
  credits: number;
  email?: string | null;
  phone?: string | null;
}) {
  if (typeof window === 'undefined' || !params.transactionId) return;
  // Google Ads dedupes by transaction_id, but we also guard locally so a remount of
  // the summary modal cannot fire the same conversion twice in one session.
  if (trackedGoogleAdsConversions.has(params.transactionId)) return;
  trackedGoogleAdsConversions.add(params.transactionId);

  requestMarketingLoader();
  ensureGoogleTags();
  const userData = setGoogleUserData({ email: params.email, phone: params.phone }) || getGoogleUserData();

  // Official Ads conversion snippet (Compra). Value is the amount actually paid.
  // user_data is attached on the event itself so enhanced conversions do not depend
  // only on a prior global set surviving until the hit is flushed.
  if (GOOGLE_ADS_CONVERSION_SEND_TO) {
    gtag('event', 'conversion', {
      send_to: GOOGLE_ADS_CONVERSION_SEND_TO,
      value: params.value,
      currency: params.currency,
      transaction_id: params.transactionId,
      ...userDataEventParams(userData),
    });
  }

  const ecommerce = {
    transaction_id: params.transactionId,
    value: params.value,
    currency: params.currency,
    items: [{
      item_id: `credits_${params.credits}`,
      item_name: `RadarBI ${params.credits} créditos`,
      quantity: 1,
      price: params.value,
    }],
  };

  if (GA4_MEASUREMENT_ID) {
    // Scoped to Analytics: an unscoped event also reaches Ads, where it could be
    // counted a second time by any action configured on the `purchase` event.
    gtag('event', 'purchase', { send_to: GA4_MEASUREMENT_ID, ...ecommerce });
  } else {
    // No gtag destination for Analytics (it is deployed through GTM instead), so the
    // container gets the same data straight from the shared dataLayer.
    const globals = window as any;
    globals.dataLayer = globals.dataLayer || [];
    globals.dataLayer.push({ event: 'purchase', ecommerce });
  }
}
